/* Brazil Cocoa: header flotante (sections/fh-header.liquid) */
if (!customElements.get('fh-header')) {
  customElements.define(
    'fh-header',
    class FhHeader extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section');
        this.card = this.querySelector('[data-card]');
        this.row = this.querySelector('.fh-header__row');
        this.drawer = this.querySelector('[data-drawer]');
        this.burger = this.querySelector('[data-drawer-open]');
        this.searchToggle = this.querySelector('[data-search-toggle]');
        this.openToggle = null;
        this.openedAt = 0;
        this.hoverTimer = null;
        this.nav = this.querySelector('.fh-nav');
        this.canHover = window.matchMedia('(hover: hover) and (min-width: 990px)');
        this.desktop = window.matchMedia('(min-width: 990px)');
        this.wideSearch = window.matchMedia('(min-width: 1440px)');

        this.onScroll = this.onScroll.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onWideSearchChange = () => this.wideSearch.matches && this.toggleSearch(false);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('click', this.onDocumentClick);
        this.wideSearch.addEventListener('change', this.onWideSearchChange);
        this.onScroll();

        if (this.nav) {
          this.checkCompact = this.checkCompact.bind(this);
          this.resizeObserver = new ResizeObserver(this.checkCompact);
          this.resizeObserver.observe(this.row);
          if (document.fonts) document.fonts.ready.then(this.checkCompact);
        }

        // Milagros-style tabs: hovering or focusing a category shows its tiles.
        this.querySelectorAll('[data-tab]').forEach((tab) => {
          const activate = () => {
            const mega = tab.closest('[data-mega]');
            mega.querySelectorAll('[data-tab]').forEach((t) => t.classList.toggle('is-active', t === tab));
            mega
              .querySelectorAll('[data-panel]')
              .forEach((panel) => panel.classList.toggle('is-active', panel.dataset.panel === tab.dataset.tab));
          };
          tab.addEventListener('mouseenter', activate);
          tab.addEventListener('focus', activate);
        });

        // Lets the product page keep its sticky column below the header.
        const setOffset = () =>
          document.documentElement.style.setProperty('--fh-sticky-offset', `${this.card.offsetHeight}px`);
        setOffset();
        if (window.ResizeObserver) new ResizeObserver(setOffset).observe(this.card);

        this.querySelectorAll('[data-mega-toggle]').forEach((toggle) => {
          toggle.addEventListener('click', () => {
            // Si el hover acaba de abrirlo, el clic no lo cierra.
            if (this.openToggle === toggle && Date.now() - this.openedAt < 400) return;
            this.openToggle === toggle ? this.closeMega() : this.openMega(toggle);
          });
        });

        this.row.addEventListener('mouseover', (event) => {
          if (!this.canHover.matches) return;
          const item = event.target.closest('.fh-nav__item');
          const toggle = item && item.querySelector(':scope > [data-mega-toggle]');
          clearTimeout(this.hoverTimer);
          if (toggle) {
            if (toggle !== this.openToggle) {
              this.hoverTimer = setTimeout(() => this.openMega(toggle), this.openToggle ? 60 : 120);
            }
          } else if (this.openToggle) {
            this.hoverTimer = setTimeout(() => this.closeMega(), 200);
          }
        });

        this.card.addEventListener('mouseleave', () => {
          if (!this.canHover.matches) return;
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => this.closeMega(), 200);
        });

        this.card.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.card.contains(event.relatedTarget)) this.closeMega();
        });

        this.querySelector('[data-backdrop]').addEventListener('click', () => {
          this.closeMega();
          this.closeDrawer();
        });

        if (this.searchToggle) {
          this.searchToggle.addEventListener('click', () => this.toggleSearch());
        }

        if (this.drawer) {
          this.burger.addEventListener('click', () => this.openDrawer());
          this.drawer.querySelector('[data-drawer-close]').addEventListener('click', () => this.closeDrawer());
        }

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          if (this.drawer && this.drawer.classList.contains('is-open')) {
            this.closeDrawer();
          } else if (this.openToggle) {
            const toggle = this.openToggle;
            this.closeMega();
            toggle.focus();
          } else if (this.classList.contains('search-open')) {
            this.toggleSearch(false);
            this.searchToggle.focus();
          }
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('click', this.onDocumentClick);
        this.wideSearch.removeEventListener('change', this.onWideSearchChange);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        document.documentElement.classList.remove('fh-lock');
      }

      // Si el menú no cabe en una línea, pasa a menú hamburguesa (CSS: .is-compact).
      checkCompact() {
        if (!this.desktop.matches) {
          this.classList.remove('is-compact');
          return;
        }
        this.classList.remove('is-compact');
        const list = this.nav.querySelector('.fh-nav__list');
        const overflows = list.scrollWidth > this.nav.clientWidth + 1;
        if (overflows) this.closeMega();
        else this.closeDrawer();
        this.classList.toggle('is-compact', overflows);
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          const stickyTop = parseFloat(getComputedStyle(this.section).top) || 0;
          const stuck = this.section.getBoundingClientRect().top <= stickyTop + 0.5 && window.scrollY > 0;
          this.classList.toggle('is-scrolled', stuck);
        });
      }

      onDocumentClick(event) {
        if (this.openToggle && !this.card.contains(event.target)) this.closeMega();
      }

      openMega(toggle) {
        if (this.openToggle === toggle) return;
        this.closeMega();
        this.toggleSearch(false);
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        toggle.setAttribute('aria-expanded', 'true');
        panel.classList.add('is-open');
        this.classList.add('mega-open');
        this.openToggle = toggle;
        this.openedAt = Date.now();
      }

      closeMega() {
        clearTimeout(this.hoverTimer);
        if (!this.openToggle) return;
        const panel = document.getElementById(this.openToggle.getAttribute('aria-controls'));
        this.openToggle.setAttribute('aria-expanded', 'false');
        panel.classList.remove('is-open');
        this.classList.remove('mega-open');
        this.openToggle = null;
      }

      toggleSearch(force) {
        if (!this.searchToggle) return;
        const open = typeof force === 'boolean' ? force : !this.classList.contains('search-open');
        if (open) this.closeMega();
        this.classList.toggle('search-open', open);
        this.searchToggle.setAttribute('aria-expanded', String(open));
        if (open) this.querySelector('.fh-search__input').focus();
      }

      openDrawer() {
        this.closeMega();
        this.toggleSearch(false);
        this.drawer.classList.add('is-open');
        this.classList.add('drawer-open');
        this.burger.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('fh-lock');
        if (typeof trapFocus === 'function') {
          trapFocus(this.drawer, this.drawer.querySelector('[data-drawer-close]'));
        } else {
          this.drawer.focus();
        }
      }

      closeDrawer() {
        if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
        this.drawer.classList.remove('is-open');
        this.classList.remove('drawer-open');
        this.burger.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('fh-lock');
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.burger);
        } else {
          this.burger.focus();
        }
      }
    }
  );
}
